import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flame } from 'lucide-react';
import { Button, Dialog, cx } from '@/components/ui';
import { navigationFromState } from '@/utils/navigateBack';
import { playClickSound } from '@/utils/soundUtils';
import CampaignOfferMarks from './CampaignOfferMarks';
import { offerUntil, offerWhat } from './campaignOfferCopy';
import { useCampaignBonusOffers } from './CampaignBonusOffersContext';
import { useShopAnnouncementOpen } from './shopAnnouncementOpen';

/** First-open ad for the current set of campaigns. Don't show this again hides that set only. */
export default function CampaignBonusPopup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { offers, popupDismissed, dismissPopup } = useCampaignBonusOffers();
  const shopAnnouncementOpen = useShopAnnouncementOpen();
  const [sessionClosed, setSessionClosed] = useState(false);
  const [saving, setSaving] = useState(false);

  if (offers.length === 0 || popupDismissed || sessionClosed || shopAnnouncementOpen) return null;

  const goAddFunds = () => {
    playClickSound();
    setSessionClosed(true);
    if (location.pathname !== '/money/add') {
      navigate('/money/add', navigationFromState(location.pathname));
    }
  };

  const hideForever = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await dismissPopup();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      title={t('campaignBonus.popupTitle', 'Deposit bonus')}
      size="md"
      placement="center"
      footer={
        <>
          <Button variant="ghost" onClick={() => void hideForever()} disabled={saving}>
            {t('campaignBonus.dontShowAgain', "Don't show this again")}
          </Button>
          <Button variant="primary" onClick={goAddFunds} disabled={saving}>
            {t('header.addMoney', 'Add money')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2" data-testid="campaign-bonus-popup">
        <div className="mb-1 flex items-center gap-2 text-warning">
          <Flame aria-hidden className="size-4 shrink-0" />
          <p className="m-0 font-ui text-small font-semibold">{t('campaignBonus.hotOffer', 'Hot offer')}</p>
        </div>
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={cx(
              'rounded-control border px-3 py-2.5',
              offer.used ? 'border-edge-subtle bg-inset' : 'border-warning/32 bg-warning/12'
            )}
          >
            <p className="m-0 mb-0.5 font-ui text-small font-semibold leading-snug text-primary">{offer.playerMessage}</p>
            <p className={cx('m-0 mb-1 font-ui text-small leading-snug', offer.used ? 'text-secondary' : 'text-warning')}>
              {offerWhat(offer)}
            </p>
            {offerUntil(offer.endsAt) ? (
              <p className="m-0 font-ui text-small text-tertiary">{offerUntil(offer.endsAt)}</p>
            ) : null}
            <CampaignOfferMarks offer={offer} />
          </div>
        ))}
      </div>
    </Dialog>
  );
}
